import { namespace } from '../helpers/namespaces';

const THEME_DATA_ATTRIBUTE = `data-${namespace}theme`;

const RADIUS_PRESETS = {
    square: { surface: '0px', button: '0px', option: '0px', container: '0px' },
    mid: { surface: '2px', button: '4px', option: '2px', container: '2px' },
    rounded: {
        surface: '32px',
        button: '999px',
        option: '24px',
        container: '16px'
    }
};

/**
 * Safely applies a CSS custom property to an element.
 *
 * @param {HTMLElement} element - The target element.
 * @param {string} key - CSS custom property key.
 * @param {string} value - CSS custom property value.
 */
function safeSetCssVar(element, key, value) {
    if (typeof value === 'string' && value.trim()) {
        element.style.setProperty(key, value);
    }
}

/**
 * Parses optional theme config from the embed's data attribute.
 *
 * @param {HTMLElement} embedElement - Team selector embed element.
 * @returns {object|null} Parsed theme config or null.
 */
export function parseThemeConfig(embedElement) {
    const rawTheme = embedElement.getAttribute(THEME_DATA_ATTRIBUTE);

    if (!rawTheme) {
        return null;
    }

    try {
        return JSON.parse(rawTheme);
    } catch (error) {
        console.warn('Invalid data-fek-theme payload', error);
        return null;
    }
}

/**
 * Applies theme config values to the embed's CSS variables and attributes.
 *
 * @param {HTMLElement} embedElement - Team selector embed element.
 * @param {object|null} themeConfig - Parsed theme config object.
 */
export function applyThemeFromEmbedConfig(embedElement, themeConfig) {
    if (!themeConfig) {
        return;
    }

    safeSetCssVar(embedElement, '--backend-bg-color', themeConfig?.brand?.bg);
    safeSetCssVar(embedElement, '--backend-fg-color', themeConfig?.brand?.fg);
    safeSetCssVar(
        embedElement,
        '--backend-font-heading',
        themeConfig?.fonts?.heading
    );
    safeSetCssVar(
        embedElement,
        '--backend-font-body',
        themeConfig?.fonts?.body
    );

    const radiusConfig =
        themeConfig?.radius ||
        (themeConfig?.radiusMode && RADIUS_PRESETS[themeConfig.radiusMode]) ||
        {};

    safeSetCssVar(
        embedElement,
        '--radius-surface-override',
        radiusConfig?.surface
    );
    safeSetCssVar(
        embedElement,
        '--radius-button-override',
        radiusConfig?.button
    );
    safeSetCssVar(
        embedElement,
        '--radius-option-override',
        radiusConfig?.option
    );
    safeSetCssVar(
        embedElement,
        '--radius-container-override',
        radiusConfig?.container
    );

    if (
        themeConfig?.themeMode === 'light' ||
        themeConfig?.themeMode === 'dark'
    ) {
        embedElement.dataset.theme = themeConfig.themeMode;
    }
}
